import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { checkEmail, checkPhone, fullPhone } from "../lib/validation";
import PhoneInput from "../components/PhoneInput";
import GoogleLoginButton from "../components/GoogleLoginButton";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
    const [errors, setErrors] = useState({});
    const [emailSuggest, setEmailSuggest] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);

    // T&C acceptance modal
    const [tcOpen, setTcOpen] = useState(false);
    const [tcChecked, setTcChecked] = useState(false);

    const validate = (field, value) => {
        const e = { ...errors };
        if (field === "name") {
            e.name = !value || value.trim().length < 2 ? "Name must be at least 2 characters" : "";
        }
        if (field === "email") {
            const r = checkEmail(value);
            e.email = r.ok ? "" : r.error;
            setEmailSuggest(r.ok && r.suggestion ? r.suggestion : "");
        }
        if (field === "phone") {
            const r = checkPhone(value);
            e.phone = value && !r.ok ? r.error : "";
        }
        if (field === "password") {
            e.password = !value || value.length < 6 ? "Password must be at least 6 characters" : "";
        }
        setErrors(e);
        return e;
    };

    const isValid = () => {
        const r = checkEmail(form.email);
        const p = checkPhone(form.phone);
        return r.ok && p.ok && form.name.trim().length >= 2 && form.password.length >= 6;
    };

    const submit = async (e) => {
        e.preventDefault();
        // Run all validations
        const all = {
            name: !form.name || form.name.trim().length < 2 ? "Name must be at least 2 characters" : "",
            email: checkEmail(form.email).ok ? "" : checkEmail(form.email).error,
            phone: checkPhone(form.phone).ok ? "" : checkPhone(form.phone).error,
            password: !form.password || form.password.length < 6 ? "Password must be at least 6 characters" : "",
        };
        setErrors(all);
        if (Object.values(all).some(Boolean)) return;
        // Open T&C modal — user must accept before account is created
        setTcChecked(false);
        setTcOpen(true);
    };

    const proceedRegister = async () => {
        if (!tcChecked) { toast.error("Please tick 'I Agree' to continue"); return; }
        setTcOpen(false);
        setLoading(true);
        try {
            const u = await register({
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: fullPhone(form.phone),
                password: form.password,
                terms_accepted_at: new Date().toISOString(),
            });
            toast.success(`Welcome, ${u.name}! 🎉`);
            navigate("/", { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (Array.isArray(detail)) {
                toast.error(detail.map(d => d.msg || JSON.stringify(d)).join(" · "));
            } else {
                toast.error(typeof detail === "string" ? detail : "Registration failed");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
            <Toaster theme="dark" richColors position="top-center" />
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="font-display text-4xl font-bold uppercase text-white">Drive or <span className="text-[#00FF66]">Kick</span></div>
                    <p className="text-slate-400 text-sm mt-2">Create your account & start booking</p>
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="register-form" noValidate>
                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Full Name</label>
                        <input value={form.name}
                            onChange={(e) => { const v = e.target.value; setForm({ ...form, name: v }); validate("name", v); }}
                            onBlur={(e) => validate("name", e.target.value)}
                            placeholder="John Doe" data-testid="register-name-input"
                            className={`w-full mt-2 h-11 px-4 bg-slate-900 border rounded text-white focus:outline-none focus:ring-2 ${
                                errors.name ? "border-red-700 focus:ring-red-500" : "border-slate-700 focus:ring-[#00FF66]"
                            }`} />
                        {errors.name && <p className="mt-1 text-xs text-red-400" data-testid="register-name-error">{errors.name}</p>}
                    </div>

                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Email</label>
                        <input type="email" value={form.email}
                            onChange={(e) => { const v = e.target.value; setForm({ ...form, email: v }); validate("email", v); }}
                            onBlur={(e) => validate("email", e.target.value)}
                            placeholder="you@email.com" data-testid="register-email-input"
                            className={`w-full mt-2 h-11 px-4 bg-slate-900 border rounded text-white focus:outline-none focus:ring-2 ${
                                errors.email ? "border-red-700 focus:ring-red-500" : "border-slate-700 focus:ring-[#00FF66]"
                            }`} />
                        {errors.email && <p className="mt-1 text-xs text-red-400" data-testid="register-email-error">{errors.email}</p>}
                        {emailSuggest && (
                            <button type="button" onClick={() => { setForm(f => ({ ...f, email: emailSuggest })); setEmailSuggest(""); validate("email", emailSuggest); }}
                                className="mt-1 text-xs text-[#00FF66] hover:underline" data-testid="email-suggestion-btn">
                                Did you mean <b>{emailSuggest}</b>?
                            </button>
                        )}
                    </div>

                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Mobile Number</label>
                        <PhoneInput value={form.phone}
                            onChange={(v) => { setForm({ ...form, phone: v }); validate("phone", v); }}
                            testid="register-phone-input"
                            error={errors.phone || null} />
                    </div>

                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Password</label>
                        <div className={`mt-2 flex items-stretch h-11 rounded overflow-hidden border ${
                            errors.password ? "border-red-700 focus-within:border-red-500" : "border-slate-700 focus-within:border-[#00FF66]"
                        } bg-slate-900`}>
                            <input type={showPwd ? "text" : "password"} value={form.password}
                                onChange={(e) => { const v = e.target.value; setForm({ ...form, password: v }); validate("password", v); }}
                                onBlur={(e) => validate("password", e.target.value)}
                                placeholder="At least 6 characters" data-testid="register-password-input"
                                className="flex-1 px-4 bg-slate-900 text-white placeholder:text-slate-600 focus:outline-none" />
                            <button type="button" onClick={() => setShowPwd(s => !s)}
                                className="px-3 text-slate-400 hover:text-white" data-testid="toggle-password-btn">
                                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {errors.password && <p className="mt-1 text-xs text-red-400" data-testid="register-password-error">{errors.password}</p>}
                    </div>

                    <button type="submit" disabled={loading || !isValid()} data-testid="register-submit-btn"
                        className="w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-wide rounded hover:bg-[#00cc52] transition active:scale-95 disabled:opacity-50 glow-green">
                        {loading ? "Creating..." : "Create account"}
                    </button>
                </form>

                <div className="my-6 flex items-center gap-3">
                    <span className="flex-1 h-px bg-slate-800" />
                    <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-500">OR</span>
                    <span className="flex-1 h-px bg-slate-800" />
                </div>

                <GoogleLoginButton label="Sign up with Google" testid="google-register-btn" />

                <p className="mt-8 text-center text-sm text-slate-400">
                    Already have an account?{" "}
                    <Link to="/login" data-testid="login-link" className="text-[#00FF66] font-semibold hover:underline">Sign in</Link>
                </p>
            </div>

            {/* T&C / Privacy acceptance modal (Mod 4) */}
            {tcOpen && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4" data-testid="tc-modal">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center flex-shrink-0">
                                <ShieldCheck className="w-5 h-5 text-[#00FF66]" />
                            </div>
                            <div>
                                <h3 className="font-display text-lg font-bold uppercase text-white">Before you continue</h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    By creating an account on Drive or Kick you agree to abide by our Terms & Conditions and acknowledge our Privacy Policy.
                                </p>
                            </div>
                        </div>
                        <div className="bg-slate-950 border border-slate-800 rounded p-3 max-h-44 overflow-y-auto text-[12px] text-slate-300 leading-relaxed">
                            <p>• Bookings are confirmed only after payment. Advance payment (50%) is supported.</p>
                            <p className="mt-1">• Cancellation: 100% refund 24+ hrs before · 50% between 24–3 hrs · no refund within 3 hrs or no-show.</p>
                            <p className="mt-1">• Misconduct at venue (alcohol, drugs, violence, gambling, smoking, vandalism, breach of school rules) results in immediate ban with <b className="text-red-400">no refund</b>.</p>
                            <p className="mt-1">• We collect name, phone, email (optional), payment info and booking history. Used for bookings, WhatsApp updates, and service improvement. Shared only with Razorpay and our WhatsApp provider.</p>
                            <p className="mt-1">
                                Full text: <Link to="/terms" target="_blank" className="text-[#00FF66] underline">Terms</Link> · <Link to="/privacy" target="_blank" className="text-[#00FF66] underline">Privacy Policy</Link>.
                            </p>
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="tc-checkbox-label">
                            <input type="checkbox" checked={tcChecked} onChange={(e) => setTcChecked(e.target.checked)}
                                data-testid="tc-checkbox"
                                className="mt-0.5 w-4 h-4 accent-[#00FF66]" />
                            <span className="text-sm text-slate-300">
                                I have read and <b className="text-white">I Agree</b> to the Terms & Conditions and Privacy Policy of Drive or Kick.
                            </span>
                        </label>
                        <div className="flex gap-2 pt-2">
                            <button type="button" onClick={() => setTcOpen(false)} data-testid="tc-cancel-btn"
                                className="flex-1 h-11 bg-slate-800 border border-slate-700 text-white font-bold uppercase tracking-widest rounded hover:bg-slate-700">
                                Cancel
                            </button>
                            <button type="button" onClick={proceedRegister} disabled={!tcChecked || loading}
                                data-testid="tc-agree-btn"
                                className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-50 disabled:cursor-not-allowed">
                                {loading ? "Creating…" : "I Agree & Continue"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
